import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { css, styled } from 'styled-components';
import * as uuid from 'uuid';
import { Loader } from '@vivaahealth/design-system';

import * as api from '../../../api';
import ChatHeader from '../Header';
import ChatInput from '../Input';
import { ConfigurationContext } from '../../../context/configurationContext';
import { Role } from 'vivaa-api/dist/models/report/feedback';
import Message from './Message';
import { fadeIn } from '../../../styles/animation';
import { ConversationContext } from '../../../context/conversationContext';
import Panel from './Message/Intent/Panel';

import notable from '../../../assets/powered-by-notable-logo.png';

const AUTOMATIC_STREAM_RETRY_NUMBER = 1;

interface Props {
  onClose: () => void;
  setIsSubmitting: (value: boolean) => void;
  isSubmitting: boolean;
}

interface ScrollingMessageInfo {
  messageId: string;
  isDone: boolean;
  isInterrupted: boolean;
}

const ChatWindow: React.FC<Props> = ({
  onClose,
  setIsSubmitting,
  isSubmitting,
}) => {
  const [inputText, setInputText] = useState('');
  const [scrollingMessageInfo, setScrollingMessageInfo] =
    useState<ScrollingMessageInfo | null>(null);
  const lastMessageRef = useRef<HTMLSpanElement>(null);
  const configurationContext = useContext(ConfigurationContext);
  const conversationContext = useContext(ConversationContext);
  const messages = conversationContext?.messages ?? [];
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const onMessageSubmit = useCallback(
    async (content: string) => {
      if (!conversationContext) return;
      if (!configurationContext?.organizationId) return;
      setIsSubmitting(true);
      setInputText('');
      try {
        let parsedData: api.Assistant.ChatResponse = {
          chat_message_id: '',
          answer: '',
          user_entities: {},
          response_entities: {},
          intent: '',
          source_documents: [],
          intentComponent: undefined,
          done: false,
        };
        conversationContext.addNewMessage({
          ...parsedData,
          intentComponent: undefined,
        });

        let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
        let retries = 0;
        let dataBuffer = '';
        let messageFullyReceived = false;
        let retryTimeout: NodeJS.Timeout | undefined;

        const streamMessage = async () => {
          if (!configurationContext?.organizationId) return;
          if (!configurationContext.id) return;
          reader = await api.client.Assistant.addMessageToConversationToStream({
            message: {
              role: Role.USER,
              content,
            },
            configurationId: configurationContext.id,
            sourceComponent: api.Assistant.SourceComponent.Hero,
            sessionId: configurationContext.sessionId,
            isEmbedded: configurationContext.isEmbedded,
          });
          while (true) {
            const { value, done } = await reader.read();
            if (!mounted.current) {
              break;
            }
            if (done) {
              if (!messageFullyReceived) {
                conversationContext.upsertLastMessage({
                  ...parsedData,
                  error: api.Assistant.MessageError.Timeout,
                  intentComponent: undefined,
                });
              }
              break;
            }
            const decodedData = new TextDecoder().decode(value);
            if (
              'logStreamResponse' in window &&
              window.logStreamResponse === true
            ) {
              console.log(decodedData);
            }
            // Messages are sometimes concatenated or chunked. Accumulate and only process complete messages
            dataBuffer += decodedData;
            const messages = dataBuffer.replaceAll('}{', '}\n{').split('\n');

            for (let i = messages.length - 1; i >= 0; i--) {
              if (!messages[i]) continue;
              try {
                parsedData = JSON.parse(messages[i]);
                dataBuffer = i === messages.length - 1 ? '' : messages[i + 1];
                if (parsedData.done) messageFullyReceived = true;
                mounted.current &&
                  conversationContext.upsertLastMessage({
                    ...parsedData,
                    intentComponent: undefined,
                  });
                if (parsedData.answer) {
                  clearTimeout(retryTimeout);
                }
                break;
              } catch (error) {
                continue;
              }
            }
          }
          setIsSubmitting(false);
        };
        retryTimeout = setTimeout(async () => {
          console.info('no data received after 5s, retrying...');
          if (!configurationContext?.organizationId) return;
          if (retries >= AUTOMATIC_STREAM_RETRY_NUMBER) return;
          retries++;
          await reader?.cancel();
          setIsSubmitting(true);
          await streamMessage();
        }, 6000);

        await streamMessage();
      } catch (error) {
        console.log(error);
        setIsSubmitting(false);
      }
    },
    [conversationContext, configurationContext, setIsSubmitting],
  );

  const handleGlobalKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!messages.length) return;
    const lastMessage = messages[messages.length - 1];
    if ('answer' in lastMessage) return;
    if (isSubmitting) return;
    onMessageSubmit(lastMessage.question);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  useEffect(() => {
    let block: ScrollLogicalPosition = 'start';
    if (!messages.length) return;
    const message = messages[messages.length - 1];
    if (
      !scrollingMessageInfo ||
      message.chat_message_id !== scrollingMessageInfo.messageId
    ) {
      setScrollingMessageInfo({
        messageId: message.chat_message_id,
        isDone: false,
        isInterrupted: false,
      });
    }
    if (message.done && !scrollingMessageInfo?.isDone && !isSubmitting) {
      setScrollingMessageInfo((info) =>
        info?.isInterrupted
          ? null
          : {
              messageId: message.chat_message_id,
              isDone: message.done,
              isInterrupted: info?.isInterrupted ?? false,
            },
      );
    } else if (message.done && !isSubmitting) {
      block = 'end';
    }
    if (scrollingMessageInfo?.isInterrupted) {
      return;
    }
    lastMessageRef.current?.scrollIntoView({
      behavior: 'smooth',
      block,
    });
    // eslint-disable-next-line
  }, [messages, isSubmitting]);

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!inputText) return;
    if (event.key !== 'Enter' || event.shiftKey) return;
    if (!conversationContext) return;
    event.preventDefault();
    conversationContext.addNewMessage({
      question: inputText,
      chat_message_id: uuid.v4(),
      done: true,
      disabled: true,
    });
  };

  return (
    <Container>
      <ChatHeader onClose={onClose} />
      <ContentContainer>
        <ConversationContainer $fullWidth={!conversationContext?.panelContent}>
          <DiscussionWrapper>
            <DiscussionContainer className="container">
              {messages
                .filter((message) => {
                  if ('hidden' in message && !!message.hidden) {
                    return false;
                  }
                  return true;
                })
                .map((message, index) => {
                  return (
                    <span
                      key={message.chat_message_id}
                      ref={
                        index === messages.length - 1 ? lastMessageRef : null
                      }
                    >
                      <Message
                        message={message}
                        showTextAnimation={
                          'answer' in message && index === messages.length - 1
                        }
                      />
                    </span>
                  );
                })}
              <LoadingDotWrapper $isHidden={!isSubmitting}>
                <LoaderDot variant="dot" />
              </LoadingDotWrapper>
            </DiscussionContainer>
          </DiscussionWrapper>

          <ActionContainer>
            <PoweredByNotable src={notable} alt="Powered by Notable" />
            <ChatInputContainer className="container">
              <ChatInput
                value={inputText}
                onChange={(newValue) => setInputText(newValue)}
                onKeyDown={(event) => handleOnKeyDown(event)}
                disabled={isSubmitting}
                autoFocus={true}
                isConversationOpened={true}
                onSubmit={() => {
                  if (!conversationContext) return;
                  if (!inputText) return;
                  conversationContext.addNewMessage({
                    question: inputText,
                    chat_message_id: uuid.v4(),
                    done: true,
                    disabled: true,
                  });
                }}
              />
            </ChatInputContainer>
          </ActionContainer>
        </ConversationContainer>
        {conversationContext?.panelContent ? (
          <Panel>{conversationContext.panelContent}</Panel>
        ) : null}
      </ContentContainer>
    </Container>
  );
};

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100000;
  display: flex;
  flex-direction: column;
  height: 100%;
  height: -webkit-fill-available;
`;

const DiscussionContainer = styled.div`
  background-color: var(--background-color);
  display: flex;
  flex-direction: column;
  height: 100%;
  height: -webkit-fill-available;
  flex-grow: 1;
`;

const ActionContainer = styled.div`
  padding-bottom: 24px;
  padding-top: 24px;
  background-color: var(--background-color);
  border-top: 1px solid var(--border-color);
  z-index: 200000;
  position: relative;

  @media (max-width: 680px) {
    padding: 0;
    border-top: none;
  }
`;

const ChatInputContainer = styled.div`
  @media (max-width: 680px) {
    padding: 0 !important;
  }
`;

const LoadingDotWrapper = styled.span.attrs<{ $isHidden: boolean }>(
  (props) => ({
    $isHidden: props.$isHidden ?? false,
  }),
)`
  animation: ${(props) =>
    props.$isHidden
      ? css`
          ${fadeIn} .4s ease-in-out reverse forwards
        `
      : 'none'};
`;

const LoaderDot = styled(Loader)`
  margin-left: 20px;
  margin-bottom: 20px;
  color: var(--font-color);
  --loader-color: var(--font-color);
  height: 8px;
  width: 8px;

  &:before,
  &:after {
    height: 8px;
    width: 8px;
    color: var(--font-color);
    --loader-color: var(--font-color);
  }
`;

const PoweredByNotable = styled.img`
  position: absolute;
  bottom: 24px;
  right: 24px;
  width: 89px;

  @media (max-width: 928px) {
    display: none;
  }
`;

const ContentContainer = styled.div`
  height: 100%;
  height: -webkit-fill-available;
  overflow: hidden;
  -webkit-box-flex: 1;
  flex-grow: 1;
  display: flex;
`;

const ConversationContainer = styled.div.attrs<{ $fullWidth: boolean }>(
  (props) => ({ $fullWidth: props.$fullWidth ?? true }),
)`
  display: flex;
  flex-direction: column;
  width: ${(props) => (props.$fullWidth ? '100%' : 'initial')};
  transition: width 0.4s ease-in-out;
`;

const DiscussionWrapper = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  height: 100%;
  background-color: var(--background-color);
`;

export default ChatWindow;
